// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullWebRegatta,
  $Model,
} from 'common.data';

// Global Enums
import {
  RegattaStatus,
} from 'common.data';
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  NavLink,
} from 'react-router-dom';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  getImageUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  formatDateRange,
} from 'common.helpers/dist/browser';
import {
  regattaStatusLabel,
} from 'common.language';
import config from '../../../config';

// Enums
import {
  statusLabelClass,
} from '../../../enums';

// Types
type $Props = $Translate & {
  basePath: string;
  item: $FullWebRegatta;
  opacity: boolean;
  shortDateFormat: string;
};

class RegattasListItemView extends React.Component<$Props> {
  renderItemButtons(item: $FullWebRegatta) {
    const {
      basePath,
      t,
    } = this.props;

    let hasTracker: boolean = false;
    let isTrackerLive: boolean = false;

    item.RegattaCourses.forEach((regattaCourse) => {
      regattaCourse.RegattaCourseRaces.forEach((regattaCourseRace) => {
        if (regattaCourseRace.Public === true) {
          hasTracker = true;

          if (regattaCourseRace.Ongoing) {
            isTrackerLive = true;
          }
        }
      });
    });

    const to: string = `${basePath}/${item.Slug || item.id}`;

    return (
      <div className={'whitespace-nowrap'}>
        {hasTracker && (
          <NavLink
            className={`btn ${isTrackerLive ? 'btn_danger' : 'btn_secondary'} btn_outlined uppercase px-2 py-1 mr-3`}
            end
            title={t('Tracker')}
            to={`${to}#${webSections[webPages.regattaPage].tracker}`}
          >
            <em className={'la la-location-arrow text-2xl'} />
          </NavLink>
        )}
        <NavLink
          className={'btn btn_primary btn_outlined uppercase px-2 py-1'}
          end
          title={t('Regatta page')}
          to={to}
        >
          <em className={'la la-flag text-2xl'} />
        </NavLink>
      </div>
    );
  }

  renderItemRegattaGroup(regattaGroup: $Model<'RegattaGroup'>) {
    const {
      t,
    } = this.props;

    const {
      AltName,
      Group,
    } = regattaGroup;

    return (
      <span
        className={'badge badge_outlined badge_secondary badge_gray disabled uppercase inline-block whitespace-nowrap mr-1 mb-1'}
        key={Group?.id}
        title={t('Regatta group')}
      >
        {AltName || Group?.Name}
      </span>
    );
  }

  renderItemRegattaGroups(item: $FullWebRegatta) {
    return item.RegattaGroups
      .filter(({
        Group,
      }) => Group)
      /* eslint-disable-next-line complexity */
      .sort((a, b) => {
        const aGroupName: string | undefined = a.AltName || a.Group?.Name;
        const bGroupName: string | undefined = b.AltName || b.Group?.Name;

        return aGroupName > bGroupName ? 1 : -1;
      })
      .map((group) => this.renderItemRegattaGroup(group));
  }

  renderItemRegattaEntries(item: $FullWebRegatta) {
    const {
      t,
    } = this.props;

    return (item.PageEnabled === true ? (
      <div className={'text-xs font-semibold uppercase'}>
        <span className={'text-blue-900'}>
          {`${t('Entries')}: `}
        </span>
        <span className={'text-gray-600'}>
          {item.RegattaEntries.length}
        </span>
      </div>
    ) : null);
  }

  renderItemRegattaGroupsAndEntries(item: $FullWebRegatta) {
    const {
      t,
    } = this.props;

    return (
      <div className={'flex flex-row items-center'}>
        <div className={'grow w-0'}>
          <div className={'flex flex-row justify-between mb-1'}>
            <div className={'text-xs text-blue-900 font-semibold uppercase'}>
              {t('Groups')}
            </div>
            {this.renderItemRegattaEntries(item)}
          </div>
          <div>
            <div className={'overflow-x-auto'}>
              <div className={'flex flex-row'}>
                {item.RegattaGroups.length > 0 && this.renderItemRegattaGroups(item)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderItemDateLocation(item: $FullWebRegatta) {
    const {
      shortDateFormat,
      t,
    } = this.props;

    const date: string = formatDateRange({
      end: item.EndDate,
      shortDateFormat,
      start: item.StartDate,
    });

    const humanDate: string = moment(item.StartDate).fromNow();
    const showHumanDate: boolean = _.includes(
      [
        RegattaStatus.upcoming,
        RegattaStatus.new,
      ],
      item.Status,
    );

    return (
      <div className={'flex flex-row lg:flex-col items-center lg:items-start text-md'}>
        <div
          className={'mr-3 lg:mr-0'}
          title={t('Regatta date')}
        >
          <div
            className={`whitespace-nowrap  ${showHumanDate === true ? 'leading-3 mt-1' : ''}`}
          >
            {date}
          </div>
          {showHumanDate === true && (
            <small className={'whitespace-nowrap text-gray-600'}>
              {humanDate}
            </small>
          )}
        </div>
        <div
          className={'font-bold lg:font-medium'}
          title={t('Regatta location')}
        >
          {item.Location}
        </div>
      </div>
    );
  }

  renderItemStatusDateLocation(item: $FullWebRegatta) {
    const {
      t,
    } = this.props;

    return (
      <div className={'flex flex-row items-center mb-2'}>
        <div className={`badge badge_outlined badge_${statusLabelClass[item.Status]} uppercase py-1 my-auto mr-3`}>
          {t(regattaStatusLabel[item.Status])}
        </div>
        <div className={'text-base flex flex-wrap flex-row lg:flex-col w-full items-center'}>
          {this.renderItemDateLocation(item)}
        </div>
      </div>
    );
  }

  renderContent(className: string) {
    const {
      item,
      opacity,
      t,
    } = this.props;

    const imageUrl: string = getImageUrl({
      cdnUrl: config.CDN_URL,
      image: item.Image,
      size: 'min',
      undefinedImage: 'regatta.png',
    });

    const opacityClass: string = opacity === true ? 'opacity-75' : '';

    return (
      <div className={`flex flex-row items-center bg-white rounded-lg py-3 px-5 ${className}`}>
        <div className={`md:hidden w-full ${opacityClass}`}>
          <div className={'flex flex-row items-center mb-2'}>
            {item.Image && (
              <div className={'flex-none w-10 mr-2'}>
                <img
                  alt={t('Logo')}
                  src={imageUrl}
                />
              </div>
            )}
            <div className={'grow'}>
              <h3 className={'text-base font-semibold'}>
                {item.Name}
              </h3>
              <div className={'text-xs text-gray-600 break-all'}>
                {item.OrganizerName}
              </div>
            </div>
            {item.PageEnabled === true && (
              <div className={'text-right pl-3'}>
                {this.renderItemButtons(item)}
              </div>
            )}
          </div>
          {this.renderItemStatusDateLocation(item)}
          {this.renderItemRegattaGroupsAndEntries(item)}
        </div>
        <div className={'hidden md:table-cell w-2/3'}>
          <div className={`flex flex-row items-center ${opacityClass} w-full`}>
            <div className={'min-w-12'}>
              <img
                alt={t('Logo')}
                className={'max-h-10'}
                src={imageUrl}
              />
            </div>
            <div className={'grow flex flex-col-reverse lg:flex-row px-3 lg:px-0'}>
              <div className={'flex flex-row items-center lg:px-3'}>
                <div className={'w-28'}>
                  <div className={`badge badge_outlined badge_${statusLabelClass[item.Status]} uppercase py-1`}>
                    {t(regattaStatusLabel[item.Status])}
                  </div>
                </div>
                <div className={'w-24'}>
                  {this.renderItemDateLocation(item)}
                </div>
              </div>
              <div className={'grow mb-2 lg:mb-0 pr-0 md:pr-3'}>
                <h3 className={'text-lg font-semibold'}>
                  {item.Name}
                </h3>
                <div className={'text-sm text-gray-600'}>
                  {item.OrganizerName}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={'hidden md:table-cell w-1/3'}>
          <div className={`flex flex-row items-center ${opacityClass} w-full`}>
            <div className={'grow'}>
              <div>
                {this.renderItemRegattaGroups(item)}
              </div>
              {this.renderItemRegattaEntries(item)}
            </div>
            {item.PageEnabled === true && (
              <div className={'text-right pl-2'}>
                {this.renderItemButtons(item)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      basePath,
      item,
      t,
    } = this.props;

    return (
      <div
        className={'RegattasListItem mb-2'}
        id={`RegattasListItem-${item.id}`}
      >
        {item.PageEnabled === true ? (
          <NavLink
            className={''}
            end
            title={t('Regatta page')}
            to={`${basePath}/${item.Slug || item.id}`}
          >
            {this.renderContent('hover:bg-blue-50')}
          </NavLink>
        ) : this.renderContent('')}
      </div>
    );
  }
}

export default RegattasListItemView;
